import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import { GiveUp } from "../components/GiveUp";
import { ResultSheet } from "../components/ResultSheet";
import { Centered, Header, Shell } from "../components/Shell";
import { Toast } from "../components/Toast";
import { haptic } from "../telegram";
import { useGame } from "../useGame";

interface Item {
  id: number;
  label: string;
  kind: "people" | "films" | "companies";
  year?: number;
}
interface View {
  category: string;
  title: string;
  /** Display order with display ids 0..n-1. */
  items: Item[];
  last: number[] | null;
  lastCorrect: boolean[] | null;
  attempts: number;
  answer?: number[];
}

const COPY = { bad_order: "Щось не так з порядком", already_guessed: "Цей порядок уже пробував" };
const KIND_ICON: Record<Item["kind"], string> = { people: "👤", films: "🎬", companies: "🏢" };
const ROW = 60; // px — card height incl. gap

export function Chrono({ date }: { date?: string | undefined }) {
  const g = useGame<View>("chrono", COPY, date);
  const state = g.screen.kind === "game" ? g.screen.state : null;
  const finished = state !== null && state.status !== "playing";
  const view = state?.view;

  // Working order (display ids) + pinned ids. Pinned cards keep their slot while
  // you drag the others around; correct cards pin themselves after a check.
  const [order, setOrder] = useState<number[] | null>(null);
  const [pinned, setPinned] = useState<Set<number>>(new Set());
  const [drag, setDrag] = useState<{ id: number; dy: number } | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const pressRef = useRef<{ id: number; y0: number; moved: boolean; startOrder: number[] } | null>(null);

  useEffect(() => {
    if (!view) return;
    if (view.answer) setOrder(view.answer);
    else if (order === null) {
      const initial = view.last ?? view.items.map((it) => it.id);
      setOrder(initial);
      if (view.last && view.lastCorrect) {
        setPinned(new Set(view.last.filter((_, pos) => view.lastCorrect?.[pos])));
      }
    }
  }, [view, order]);

  if (g.screen.kind === "loading") return <Shell><Centered>…</Centered></Shell>;
  if (g.screen.kind === "error") {
    return (
      <Shell>
        <Centered>
          <div>{g.screen.message}</div>
          {g.screen.retry && (
            <button type="button" onClick={() => void g.load()} className="rounded-xl bg-tgbutton px-5 py-2.5 text-sm font-semibold text-tgbuttontext">
              ще раз
            </button>
          )}
        </Centered>
      </Shell>
    );
  }
  const s = g.screen.state;
  const v = s.view;
  const cur = order ?? v.items.map((it) => it.id);
  const byId = new Map(v.items.map((it) => [it.id, it]));
  const unchanged = v.last !== null && cur.join(",") === v.last.join(",");

  /** Move `id` to slot `target` among the unpinned slots; pinned cards stay put. */
  const moveTo = (base: number[], id: number, target: number): number[] => {
    const free = base.map((x, i) => (pinned.has(x) ? -1 : i)).filter((i) => i >= 0);
    const movable = base.filter((x) => !pinned.has(x));
    const from = movable.indexOf(id);
    if (from < 0) return base;
    // target is an absolute slot; clamp to the nearest free slot and convert to movable index
    let ti = 0;
    let best = Infinity;
    free.forEach((slot, k) => {
      const d = Math.abs(slot - target);
      if (d < best) {
        best = d;
        ti = k;
      }
    });
    const next = [...movable];
    next.splice(from, 1);
    next.splice(ti, 0, id);
    const out = [...base];
    free.forEach((slot, k) => {
      out[slot] = next[k] as number;
    });
    return out;
  };

  const onPointerDown = (e: React.PointerEvent, id: number) => {
    if (finished) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    pressRef.current = { id, y0: e.clientY, moved: false, startOrder: cur };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const p = pressRef.current;
    if (!p) return;
    const dy = e.clientY - p.y0;
    if (!p.moved && Math.abs(dy) < 6) return;
    if (!p.moved) {
      p.moved = true;
      haptic.tap();
    }
    if (pinned.has(p.id)) return; // pinned: no drag
    const startIdx = p.startOrder.indexOf(p.id);
    const target = Math.max(0, Math.min(cur.length - 1, Math.round(startIdx + dy / ROW)));
    const next = moveTo(p.startOrder, p.id, target);
    if (next.join(",") !== cur.join(",")) {
      haptic.tap();
      setOrder(next);
    }
    // visual offset relative to the card's *current* slot
    const nowIdx = next.indexOf(p.id);
    setDrag({ id: p.id, dy: dy - (nowIdx - startIdx) * ROW });
  };
  const onPointerUp = (id: number) => {
    const p = pressRef.current;
    pressRef.current = null;
    setDrag(null);
    if (p && !p.moved && !finished) {
      // tap = pin / unpin
      setPinned((prev) => {
        const n = new Set(prev);
        if (n.has(id)) n.delete(id);
        else n.add(id);
        return n;
      });
      haptic.tap();
    }
  };

  const submit = async () => {
    const next = await g.run(() => api.guess<View>("chrono", cur.join(","), date));
    if (next && next.status === "playing") {
      const correct = (next.extra as { correct?: boolean[] } | undefined)?.correct;
      if (correct) setPinned(new Set(cur.filter((_, pos) => correct[pos])));
    }
  };

  return (
    <Shell header={<Header left={`#${s.number}`} title="CHRONO" right={`🔥 ${s.me.streak}`} />}>
      <div className="px-4 pb-1 text-center text-sm font-semibold">{v.title}</div>
      <div className="px-4 pb-2 text-center text-[11px] text-tghint">
        зверху — найраніше. тягни картки · тап = 📌 закріпити · спроб: {v.attempts}
      </div>

      <div ref={listRef} className="flex-1 overflow-y-auto px-4 pb-2" style={{ touchAction: finished ? "auto" : "none" }}>
        <div className="relative" style={{ height: cur.length * ROW }}>
          {cur.map((id, pos) => {
            const it = byId.get(id);
            const mark = finished ? true : unchanged ? (v.lastCorrect?.[pos] ?? null) : null;
            const isPinned = pinned.has(id) && !finished;
            const tone = mark === true ? "bg-ok/25" : mark === false ? "bg-cold/15" : isPinned ? "bg-[rgba(128,128,128,0.28)]" : "bg-tgsection";
            const dragging = drag?.id === id;
            const y = pos * ROW + (dragging ? drag.dy : 0);
            return (
              <div
                key={id}
                onPointerDown={(e) => onPointerDown(e, id)}
                onPointerMove={onPointerMove}
                onPointerUp={() => onPointerUp(id)}
                onPointerCancel={() => onPointerUp(id)}
                className={`absolute left-0 right-0 flex select-none items-center gap-3 rounded-lg px-3 ${tone} ${dragging ? "z-10 shadow-lg ring-2 ring-tgbutton/60" : "transition-transform duration-150"}`}
                style={{ transform: `translateY(${y}px)`, height: ROW - 6 }}
              >
                <div className="w-5 text-center text-xs text-tghint">{pos + 1}</div>
                <div className="text-lg">{KIND_ICON[it?.kind ?? "people"]}</div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[15px] font-semibold">{it?.label ?? "?"}</div>
                  {it?.year !== undefined && <div className="text-xs text-tghint">{it.year}</div>}
                </div>
                {!finished && (isPinned ? <span className="text-base">📌</span> : <span className="text-tghint">⋮⋮</span>)}
              </div>
            );
          })}
        </div>
      </div>

      {!finished && (
        <div className="flex gap-2 px-4 pb-[calc(var(--tg-safe-area-inset-bottom,0px)+10px)] pt-1">
          <button
            type="button"
            disabled={g.busy || unchanged}
            onClick={() => void submit()}
            className="flex-[2] rounded-xl bg-tgbutton py-3 text-sm font-bold text-tgbuttontext active:brightness-90 disabled:opacity-35"
          >
            {g.busy ? "…" : "Перевірити"}
          </button>
          <GiveUp busy={g.busy} onGiveUp={() => void g.run(() => api.giveUp<View>("chrono", date))} />
        </div>
      )}

      <Toast message={g.toast} />
      {g.sheetOpen && finished && (
        <ResultSheet
          state={s}
          archive={Boolean(date)}
          title={s.status === "won" ? "Є" : "Здався"}
          subtitle={s.status === "won" ? `${v.attempts} ${v.attempts === 1 ? "спроба" : "спроб"} · стрік ${s.me.streak} 🔥` : "стрік згорів"}
          scoreLabel="avg спроб"
          formatScore={(n) => `${n}`}
          onDismiss={() => g.setSheetOpen(false)}
        />
      )}
    </Shell>
  );
}
